import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AlertTriangle, X } from "lucide-react";
import { Button, FilterChips, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select, Textarea } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { InternalUserPicker } from "@/components/users/InternalUserPicker";
import { AttachResourceModal } from "@/components/messaging/AttachResourceModal";
import { taskTypeForMessageType } from "@/types/recordTypeBridge";
import { useUserTimeZone } from "@/utils/datetime";
import { useOptionalAuth } from "@/hooks/useAuth";
import { formatDateInTz, formatTimeInTz } from "@/design-system/lib/format";
import { extractErrorMessage } from "@/api/client";
import {
  fallbackUserLabel,
  useInternalUserLabels,
} from "@/api/internalUsers";
import type {
  AppointmentAvailabilityRequest,
  AppointmentConflictCheckRequest,
  AppointmentDto,
  BusyBlockDto,
  ConflictingAppointmentDto,
  CreateAppointmentDto,
  ExternalAttendeeDto,
  ParsedAppointmentWithClassificationDto,
  RecurrenceConflictRequest,
  RecurrenceEditScope,
  RecurrenceRequestDto,
  UpdateAppointmentDto,
  UserRecurrenceConflictDto,
} from "@/api/appointments";
import type { WorkTaskRelatedEntityType } from "@/api/workTasks";
import {
  useAvailability,
  useConflictCheck,
  useCreateAppointment,
  useRecurrenceConflicts,
  useUpdateAppointment,
} from "../hooks/useAppointments";
import { ExternalAttendeeEditor } from "./ExternalAttendeeEditor";
import { RepeatControl } from "./RepeatControl";
import { RecurrenceScopeDialog } from "./RecurrenceScopeDialog";
import {
  AttendeeAvailabilityStrip,
  type AvailabilityRow,
} from "./AttendeeAvailabilityStrip";
import { nextDayIso } from "../lib/availability";
import {
  addMinutesToWallClock,
  DURATION_PRESETS,
  formatDurationLabel,
  presetIdForMinutes,
  shiftEndForStartChange,
  wallClockDeltaMinutes,
} from "../lib/duration";
import {
  buildRecurrenceRequest,
  presetForRule,
  presetRule,
  type RecurrenceEnd,
  type RepeatPresetId,
  type RuleCore,
} from "../lib/recurrence";
import {
  appointmentDayZone,
  utcIsoToDateInput,
  utcIsoToTimeInput,
  wallClockToUtcIso,
} from "../lib/tz";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set, the modal edits this appointment; otherwise it creates one. */
  appointment?: AppointmentDto;
  /** Pre-selected calendar date (`YYYY-MM-DD`) for a fresh appointment. */
  defaultDate?: string;
  /**
   * AI Create+ — pre-populate a fresh appointment from a parsed draft.
   *
   * `pastedSource` is an intersection rather than a member of
   * `ParsedAppointmentWithClassificationDto`, so that type stays a faithful
   * mirror of the server DTO: the text the user pasted is a client-side fact
   * the panel already holds, not something the parse endpoint returns.
   */
  prefill?: ParsedAppointmentWithClassificationDto & { pastedSource?: string };
  /** AI Create+ — rendered below the form (e.g. the Lead suggestion section). */
  extraContent?: ReactNode;
  /**
   * AI Create+ — when set, replaces the normal create call. Receives the built
   * {@link CreateAppointmentDto} so the caller can orchestrate (e.g. create a
   * Lead and link it) before creating the appointment, then close the panel.
   * The override owns its own success/error toasts and close.
   */
  onSubmitOverride?: (payload: CreateAppointmentDto) => Promise<void>;
  /** AI Create+ — when set, renders a "Back" button (returns to the paste step). */
  onBack?: () => void;
  /**
   * Entity Calendar tab — pre-fills the related-entity link and LOCKS it. Named
   * and semantically matched to `QuickAddTaskModal`'s
   * `prefilledRelatedType` / `prefilledRelatedEntityId` /
   * `prefilledRelatedEntityLabel` so the two "create from inside the entity"
   * surfaces read the same way.
   *
   * Only the label is carried, not a detail URL: on an entity page the link
   * already points at the record the user is looking at, so the chip has
   * nowhere useful to navigate — and a click that unmounted this modal would
   * silently discard a half-filled appointment, since the shared Modal funnels
   * every close path into one `onClose` and tracks no dirty state.
   *
   * All three are optional and additive. With none passed the modal behaves
   * exactly as it did before: the calendar page and the AI Create+ panel are
   * unaffected, and the remove (x) affordance stays available.
   */
  prefilledRelatedType?: WorkTaskRelatedEntityType;
  prefilledRelatedEntityId?: string;
  prefilledRelatedEntityLabel?: string;
}

/**
 * Minutes-before options for the reminder Select.
 *
 * 🔴 EXPORTED so the longest lead time is pinnable. It is one half of a wall
 * with `AppointmentReminderWorker`: that worker remembers a send for 24 hours
 * when deciding whether to send again, and its own doc comment names THIS array
 * as the reason 24 hours is sufficient. Add a longer option (a week, say)
 * without widening the worker's dedup window to match and every recipient of
 * that reminder is reminded a second time — silently, with nothing failing.
 * The server-side cap belongs in a named constant (`MaxReminderLeadMinutes`);
 * until that lands, `AppointmentReminderWorker.DedupWindow` is the other side.
 */
export const REMINDER_OPTIONS: { label: string; value: number | "" }[] = [
  { label: "None", value: "" },
  { label: "5 minutes before", value: 5 },
  { label: "10 minutes before", value: 10 },
  { label: "15 minutes before", value: 15 },
  { label: "30 minutes before", value: 30 },
  { label: "1 hour before", value: 60 },
  { label: "1 day before", value: 1440 },
];

const DEFAULT_START_TIME = "09:00";
const DEFAULT_END_TIME = "10:00";

/**
 * Debounce for the informational attendee reads. The conflict check and the
 * availability strip share it so both fire on the same tick and resolve in
 * parallel — the strip adds no latency on top of the badge.
 */
const CONFLICT_DEBOUNCE_MS = 400;

/**
 * The two informational reads for the current draft. Bundled into one debounced
 * value (rather than two timers) so they always fire together.
 */
interface DraftLookups {
  conflict: AppointmentConflictCheckRequest | null;
  availability: AppointmentAvailabilityRequest | null;
  /**
   * D7 — the bounded busy check across a proposed repeat rule's next few
   * occurrences. Null unless a repeat rule is set, so a one-off appointment
   * issues exactly the two requests it always has. Riding the SAME debounced
   * value as the other two is what keeps that true: it cannot fire on its own
   * tick, and it cannot fire without them.
   */
  recurrence: RecurrenceConflictRequest | null;
}

const NO_LOOKUPS: DraftLookups = {
  conflict: null,
  availability: null,
  recurrence: null,
};

/**
 * A fresh appointment repeats until the platform's horizon unless told
 * otherwise. `endAfterOccurrences` is seeded rather than left null so switching
 * to "After a number of times" has a sensible value in the box already.
 */
const DEFAULT_RECURRENCE_END: RecurrenceEnd = {
  endMode: "Never",
  endAfterOccurrences: 10,
  endOnLocalDate: null,
};

/**
 * What the Custom… builder opens on. Reuses the shared "Weekly on <anchor day>"
 * preset so the builder and the preset row can never disagree about what a
 * weekly rule looks like; the literal fallback covers a Start date that is
 * absent or half-typed (reachable through AI Create+ when the parse found no
 * date), where there is no weekday to anchor on.
 */
function defaultCustomRule(startDate: string): RuleCore {
  return presetRule("weekly", startDate) ?? { frequency: "Weekly", interval: 1, byWeekday: "MO" };
}

/**
 * The rule's SHAPE plus how it ends, as a comparable string — what an edit
 * checks to decide whether the repeat rule itself changed.
 *
 * 🔴 The ANCHOR is deliberately excluded. Moving a series' Start is expressed
 * by `startAt`/`endAt` plus the chosen `scope`; folding the anchor in here would
 * make every Start edit ALSO a wholesale rule replacement, which flatly
 * contradicts a "this occurrence" scope.
 *
 * 🔴 This sentence used to end "and the server applies it that way", and the
 * server did not: the sibling loop copied content only and named regeneration
 * as timing's owner, while regeneration was reached only when the repeat
 * controls themselves were edited — which this exclusion guarantees they were
 * not. A moved hour therefore landed on the edited row and nowhere else. The
 * server DOES apply it that way now (AppointmentService.PropagateTimingAsync),
 * and this payload is unchanged: it was always sufficient.
 */
function ruleShapeKey(core: RuleCore | null, end: RecurrenceEnd): string {
  if (!core) return "";
  return [
    core.frequency,
    core.interval,
    core.byWeekday ?? "",
    core.monthlyMode ?? "",
    core.monthlyDayOfMonth ?? "",
    core.monthlyWeekdayOrdinal ?? "",
    core.monthlyWeekday ?? "",
    end.endMode,
    end.endMode === "AfterOccurrences" ? end.endAfterOccurrences ?? "" : "",
    end.endMode === "OnDate" ? end.endOnLocalDate ?? "" : "",
  ].join("|");
}

/** A stored `RecurrenceRequestDto` split back into the two pieces the form edits. */
function splitStoredRule(rule: RecurrenceRequestDto | null | undefined): {
  core: RuleCore | null;
  end: RecurrenceEnd;
} {
  if (!rule) return { core: null, end: DEFAULT_RECURRENCE_END };
  return {
    core: {
      frequency: rule.frequency,
      interval: rule.interval,
      byWeekday: rule.byWeekday ?? null,
      monthlyMode: rule.monthlyMode ?? null,
      monthlyDayOfMonth: rule.monthlyDayOfMonth ?? null,
      monthlyWeekdayOrdinal: rule.monthlyWeekdayOrdinal ?? null,
      monthlyWeekday: rule.monthlyWeekday ?? null,
    },
    end: {
      endMode: rule.endMode,
      endAfterOccurrences: rule.endAfterOccurrences ?? DEFAULT_RECURRENCE_END.endAfterOccurrences,
      endOnLocalDate: rule.endOnLocalDate ?? null,
    },
  };
}

/**
 * A short tooltip describing a person's first overlapping appointment. Uses the
 * title when the API supplied one (Appointments.ViewAll holders) and falls back
 * to a busy time-range otherwise.
 */
function describeConflict(
  conflicts: ConflictingAppointmentDto[],
  tz: string | null | undefined,
): string {
  const first = conflicts[0];
  const base = first.title
    ? first.title
    : first.isAllDay
      ? "Busy all day"
      : `Busy ${formatTimeInTz(first.startUtc, tz)} – ${formatTimeInTz(first.endUtc, tz)}`;
  return conflicts.length > 1 ? `${base} (+${conflicts.length - 1} more)` : base;
}

/** The single non-blocking summary line shown above Create/Save. */
function buildConflictSummary(
  memberCount: number,
  organizerFlagged: boolean,
): string | null {
  if (memberCount > 0 && organizerFlagged) {
    return `You and ${memberCount} team member${memberCount > 1 ? "s" : ""} have a scheduling conflict during this time.`;
  }
  if (memberCount > 0) {
    return `${memberCount} team member${memberCount > 1 ? "s" : ""} ${
      memberCount > 1 ? "have" : "has"
    } a scheduling conflict during this time.`;
  }
  if (organizerFlagged) {
    return "You have a scheduling conflict during this time.";
  }
  return null;
}

/**
 * The divider between the AI's summary and the text it was written from. One
 * line, visually distinct, and stable — it is what makes the block findable in
 * a Description a month later.
 */
const PASTED_SOURCE_DIVIDER = "——— Pasted source ———";

/**
 * `CalendarAppointment.VideoLink`'s column width. Checked here so the user is
 * told which field and what the limit is while the value is still in front of
 * them; the server enforces the same limit independently
 * (`AppointmentFieldLengths.VideoLink`), because a client check is a courtesy
 * and not a guarantee.
 *
 * Deliberately NOT a `maxLength` on the input: that truncates a long paste
 * silently, with no event and no error, and the user leaves with a link that
 * looks present and does not work — which is this PR's own bug moved one layer
 * up. The over-length value stays in the field so it can be seen and fixed.
 */
const MAX_VIDEO_LINK_LENGTH = 500;

/**
 * The review step's Description: the AI's summary, a divider, then the text the
 * user pasted. Lands in the textarea BEFORE save, so it can be read, edited or
 * deleted like anything else on the form.
 *
 * Composed in the hydration effect rather than at submit time or in a second
 * effect, which is what makes a re-parse REPLACE the block instead of appending
 * a second one: a new parse produces a new `prefill` identity, the effect
 * re-runs, and `setDescription` overwrites. (The review form also unmounts on
 * "Back to paste" — the panel renders a different element there — so both
 * mechanisms hold independently.)
 *
 * The source is trimmed only at its ends. Interior content is untouched:
 * `Description` is `nvarchar(max)`, so there is no length to fit and nothing to
 * gain from reshaping what someone pasted.
 */
function composeDescriptionWithSource(
  aiDescription: string | null | undefined,
  pastedSource: string | null | undefined,
): string {
  const summary = (aiDescription ?? "").trim();
  const source = (pastedSource ?? "").trim();
  if (!source) return summary;
  const block = `${PASTED_SOURCE_DIVIDER}\n\n${source}`;
  // With no AI summary the block still appears, with nothing above it.
  return summary ? `${summary}\n\n${block}` : block;
}

export function AppointmentFormModal({
  open,
  onClose,
  appointment,
  defaultDate,
  prefill,
  extraContent,
  onSubmitOverride,
  onBack,
  prefilledRelatedType,
  prefilledRelatedEntityId,
  prefilledRelatedEntityLabel,
}: Props) {
  const tz = useUserTimeZone();
  const { toast } = useToast();
  const create = useCreateAppointment();
  const update = useUpdateAppointment(appointment?.id ?? "");
  const isEdit = Boolean(appointment);
  const auth = useOptionalAuth();
  const organizerId = auth?.user?.id ?? null;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const [dialIn, setDialIn] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  // Whether internal attendees get the invitation by email on top of the in-app
  // notice. Hydrated in ALL THREE branches of the effect below — a state that is
  // set in only some of them carries a stale value across a re-open, which is
  // the failure the effect exists to prevent.
  const [emailInternal, setEmailInternal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState(DEFAULT_END_TIME);
  const [reminder, setReminder] = useState<number | "">("");
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);
  const [externalAttendees, setExternalAttendees] = useState<ExternalAttendeeDto[]>([]);
  const [linked, setLinked] = useState<{
    type: WorkTaskRelatedEntityType;
    id: string;
    label: string;
    /**
     * The linked record's detail route, so the chip can be clicked through to
     * it. Both sources already resolve one — the server on read, and the
     * picker on select — so nothing here derives a route from (type, id).
     * Null only when the record was deleted after it was linked, in which case
     * the resolver reports "(deleted)" with no URL and the chip stays static.
     */
    detailUrl: string | null;
  } | null>(null);

  /**
   * Entity Calendar tab — the pre-seeded, non-removable link, or null when the
   * caller passed none (every pre-existing consumer). `detailUrl` is
   * deliberately null: the chip must not become a navigation target here, since
   * the link points at the page the user is already on and a click would
   * unmount the modal and discard a half-filled appointment.
   */
  const lockedLink = useMemo(
    () =>
      prefilledRelatedType && prefilledRelatedEntityId
        ? {
            type: prefilledRelatedType,
            id: prefilledRelatedEntityId,
            label: prefilledRelatedEntityLabel ?? prefilledRelatedType,
            detailUrl: null,
          }
        : null,
    [prefilledRelatedType, prefilledRelatedEntityId, prefilledRelatedEntityLabel],
  );

  /**
   * Whether the chip hides its remove affordance. Scoped to CREATE: an edit
   * hydrates its link from the appointment, so a caller that passed both would
   * otherwise lock a link it didn't supply.
   */
  const linkLocked = lockedLink !== null && !appointment;

  // --- Repeats ---------------------------------------------------------------
  // `repeatPreset` is the row the user picked; `customRule` is what the Custom…
  // builder holds, kept separately so switching away from Custom… and back does
  // not lose it. NOTHING here is derived by an effect — the preset LABELS and
  // the rule itself are computed at render from the current Start date.
  const [repeatPreset, setRepeatPreset] = useState<RepeatPresetId>("none");
  const [customRule, setCustomRule] = useState<RuleCore>(() => defaultCustomRule(""));
  const [recurrenceEnd, setRecurrenceEnd] = useState<RecurrenceEnd>(DEFAULT_RECURRENCE_END);
  /**
   * The rule this appointment was opened with, as a shape key. An edit sends a
   * `recurrence` (or `clearRecurrence`) only when the current shape differs, so
   * saving an untouched appointment carries neither — which is what keeps a
   * one-off save byte-identical to what it has always sent.
   */
  const [initialRuleKey, setInitialRuleKey] = useState("");
  /**
   * The built update payload, parked while the scope dialog asks which
   * occurrences it applies to. Non-null ONLY for an appointment that is part of
   * a series; a one-off never sets it and never sees the dialog.
   */
  const [pendingUpdate, setPendingUpdate] = useState<UpdateAppointmentDto | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [debouncedLookups, setDebouncedLookups] =
    useState<DraftLookups>(NO_LOOKUPS);

  // The zone THIS appointment's calendar day is written in. An all-day
  // appointment is a wall-calendar value stored as two instants, so its day
  // belongs to the author; a timed one is a real instant and belongs to the
  // viewer. `appointmentDayZone` returns `tz` for every timed appointment, so
  // everything below is byte-identical for one.
  //
  // 🔴 ONE VALUE, USED ON BOTH SIDES. Hydration reads a day out of the stored
  // instants and the compose sites write one back. If those two resolved
  // different zones, a save that changed nothing but the title would MOVE the
  // appointment — strictly worse than the mis-rendering this closes. Derived
  // from the `isAllDay` STATE rather than the DTO's, because the user can tick
  // the box on an appointment that was timed a moment ago; the hydration effect
  // reads the DTO's own facts instead, because at that point the state is what
  // it is about to set.
  const dayZone = appointmentDayZone(
    { isAllDay, authoredTimeZoneId: appointment?.authoredTimeZoneId },
    tz,
  );

  // Hydrate state on open so a re-open never carries stale values.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setPickerOpen(false);
    setWarningDismissed(false);
    // Drop any stale conflict/availability result from a previous open; both
    // re-compute from the freshly hydrated state below.
    setDebouncedLookups(NO_LOOKUPS);
    setPendingUpdate(null);
    if (appointment) {
      // The zone this appointment's day is read in — see `dayZone` above. For
      // an all-day row, stored as authored-midnight through authored-23:59,
      // this is the difference between hydrating the day the author picked and
      // one a day either side of it; for a timed row it IS the viewer's zone.
      // Derived from the DTO's own facts rather than the `isAllDay` state,
      // which this block is about to set.
      const hydrateZone = appointmentDayZone(appointment, tz);
      // The repeat rule, split back into the preset row + the end condition, so
      // an edit re-opens on the row the author picked rather than dropping every
      // saved series into Custom…. `presetForRule` needs the anchor date, which
      // is this appointment's own start read in that zone — the same value the
      // Start field is about to be hydrated with.
      const startDay = utcIsoToDateInput(appointment.startAt, hydrateZone);
      const { core, end } = splitStoredRule(appointment.recurrence?.rule);
      setRepeatPreset(presetForRule(core, startDay));
      setCustomRule(core ?? defaultCustomRule(startDay));
      setRecurrenceEnd(end);
      setInitialRuleKey(ruleShapeKey(core, end));

      setTitle(appointment.title);
      setDescription(appointment.description ?? "");
      setLocation(appointment.location ?? "");
      setVideoLink(appointment.videoLink ?? "");
      setDialIn(appointment.dialInInstructions ?? "");
      setIsAllDay(appointment.isAllDay);
      setEmailInternal(appointment.emailInternalAttendees ?? false);
      setStartDate(utcIsoToDateInput(appointment.startAt, hydrateZone));
      setStartTime(
        utcIsoToTimeInput(appointment.startAt, hydrateZone) || DEFAULT_START_TIME,
      );
      // The END matters as much as the start and fails the other way round: an
      // all-day row ends at authored-zone 23:59, which a viewer EAST of the
      // author reads as the following day. Hydrated in the viewer's zone, a
      // one-day appointment opened from Tokyo showed Start the 20th / End the
      // 21st — and saving that wrote a genuinely two-day appointment.
      setEndDate(utcIsoToDateInput(appointment.endAt, hydrateZone));
      setEndTime(
        utcIsoToTimeInput(appointment.endAt, hydrateZone) || DEFAULT_END_TIME,
      );
      setReminder(appointment.reminderMinutesBefore ?? "");
      setAttendeeIds(appointment.attendees.map((a) => a.userId));
      setExternalAttendees(appointment.externalAttendees ?? []);
      setLinked(
        appointment.relatedEntity
          ? {
              type: appointment.relatedEntity.type,
              id: appointment.relatedEntity.id,
              label: appointment.relatedEntity.displayName,
              detailUrl: appointment.relatedEntity.detailUrl,
            }
          : null,
      );
    } else if (prefill) {
      const startDay = prefill.startUtc ? utcIsoToDateInput(prefill.startUtc, tz) : "";
      // A repeat phrase the parser recognised. It carries no anchor by design
      // ("the form supplies that"), so it lands as a rule shape and the Start
      // date above anchors it. Without this the parse is silently discarded and
      // the form reads "Does not repeat" over text that said "every Tuesday".
      const parsedCore: RuleCore | null = prefill.recurrence
        ? {
            frequency: prefill.recurrence.frequency,
            interval:
              prefill.recurrence.interval && prefill.recurrence.interval > 0
                ? prefill.recurrence.interval
                : 1,
            byWeekday: prefill.recurrence.byWeekday ?? null,
            monthlyMode: prefill.recurrence.monthlyMode ?? null,
            monthlyDayOfMonth: prefill.recurrence.monthlyDayOfMonth ?? null,
          }
        : null;
      setRepeatPreset(parsedCore ? presetForRule(parsedCore, startDay) : "none");
      setCustomRule(parsedCore ?? defaultCustomRule(startDay));
      setRecurrenceEnd(DEFAULT_RECURRENCE_END);
      // Create mode: there is no stored rule to differ from.
      setInitialRuleKey("");

      setTitle(prefill.title);
      setDescription(composeDescriptionWithSource(prefill.description, prefill.pastedSource));
      setLocation(prefill.location ?? "");
      setVideoLink(prefill.videoLink ?? "");
      setDialIn(prefill.dialInInstructions ?? "");
      setIsAllDay(false);
      setEmailInternal(false);
      setStartDate(startDay);
      setStartTime(
        prefill.startUtc
          ? utcIsoToTimeInput(prefill.startUtc, tz) || DEFAULT_START_TIME
          : DEFAULT_START_TIME,
      );
      setEndDate(prefill.endUtc ? utcIsoToDateInput(prefill.endUtc, tz) : startDay);
      setEndTime(
        prefill.endUtc
          ? utcIsoToTimeInput(prefill.endUtc, tz) || DEFAULT_END_TIME
          : DEFAULT_END_TIME,
      );
      setReminder(prefill.reminderMinutesBefore ?? "");
      setAttendeeIds(prefill.internalAttendees.map((a) => a.userId));
      setExternalAttendees(
        prefill.externalAttendees.map((a) => ({
          name: a.name,
          email: a.email,
          notes: a.notes ?? null,
        })),
      );
      setLinked(lockedLink);
    } else {
      const day = defaultDate ?? "";
      setRepeatPreset("none");
      setCustomRule(defaultCustomRule(day));
      setRecurrenceEnd(DEFAULT_RECURRENCE_END);
      setInitialRuleKey("");

      setTitle("");
      setDescription("");
      setLocation("");
      setVideoLink("");
      setDialIn("");
      setIsAllDay(false);
      setEmailInternal(false);
      setStartDate(day);
      setStartTime(DEFAULT_START_TIME);
      setEndDate(day);
      setEndTime(DEFAULT_END_TIME);
      setReminder("");
      setAttendeeIds([]);
      setExternalAttendees([]);
      setLinked(lockedLink);
    }
  }, [open, appointment, defaultDate, prefill, tz, lockedLink]);

  // --- Duration snap: End follows Start -------------------------------------
  // Moving Start carries End with it so the duration the user set is what
  // survives. Every mainstream calendar behaves this way; without it, dragging a
  // 9-10 meeting to 2 PM silently leaves a 2 PM - 10 AM range behind.
  //
  // This deliberately lives in the Start inputs' onChange handlers and NOT in an
  // effect watching [startDate, startTime]. The only other writers to these four
  // fields are the three branches of the hydration effect above, so "the user
  // edited Start" is structurally distinguishable right here and needs no flag,
  // ref, or provenance tracking. An effect would also fire on every hydration
  // and on any tz/defaultDate change, and would then need exactly the machinery
  // this placement makes unnecessary. If a change here ever seems to want a flag
  // or an effect, the design has drifted.
  const applyStartChange = (nextDate: string, nextTime: string) => {
    const shiftedEnd = shiftEndForStartChange(
      { startDate, startTime, endDate, endTime },
      { startDate: nextDate, startTime: nextTime },
    );
    setStartDate(nextDate);
    setStartTime(nextTime);
    // Null means there is no duration to carry: Start is still being typed (a
    // native picker reports "" until it's complete) or End's time was cleared.
    // Either way End is left exactly as the user left it.
    if (shiftedEnd) {
      setEndDate(shiftedEnd.date);
      setEndTime(shiftedEnd.time);
    }
    // React 19 batches all of these into one re-render, so `liveLookups` below
    // recomputes once and the shared debounce still issues a single
    // conflict-check + availability pair per edit — the same count as before.
  };

  /** Set End from a duration quick-select chip. */
  const applyDuration = (minutes: number) => {
    const end = addMinutesToWallClock(startDate, startTime, minutes);
    // A no-op when Start doesn't parse — reachable via AI Create+ when the parse
    // found no date, which leaves both dates empty. `FilterChips` has no
    // disabled state and forking the shared component to add one isn't worth it.
    if (!end) return;
    setEndDate(end.date);
    setEndTime(end.time);
  };

  // The duration the fields currently express. `endDate || startDate` mirrors
  // the two existing read sites (`liveLookups` and `submit`), where an absent
  // end date already means "the start date".
  const durationMinutes = wallClockDeltaMinutes(
    startDate,
    startTime,
    endDate || startDate,
    endTime,
  );
  const activeDurationId = presetIdForMinutes(durationMinutes);
  // Shown only for a computable, strictly positive, non-preset duration. It
  // fills the void when no chip is lit — which would otherwise read as "nothing
  // selected, is this broken?" — and is the only way a multi-day span is
  // legible at all. Zero and negative are suppressed: a muted "-40m" looks like
  // a rendering bug, and submit's inline error owns that state.
  const durationReadout =
    activeDurationId === "" ? formatDurationLabel(durationMinutes) : "";

  // --- The repeat rule ------------------------------------------------------
  // Derived at RENDER from the preset row plus the current Start date — never
  // written into state by an effect watching [startDate, startTime]. That is
  // the same rule the duration snap above follows, and for the same reasons:
  // an effect would fire on every hydration and on any tz/defaultDate change,
  // and would then need the flag/ref machinery its absence makes unnecessary.
  //
  // Memoised because `liveLookups` depends on it: a fresh object every render
  // would re-arm the shared debounce every render and break the one-request-
  // per-changed-window guarantee the invariant tests pin.
  const repeatRule = useMemo<RuleCore | null>(
    () => (repeatPreset === "custom" ? customRule : presetRule(repeatPreset, startDate)),
    [repeatPreset, customRule, startDate],
  );

  // --- D16: a repeat and an external attendee are mutually exclusive ---------
  // 🔴 A COURTESY, NOT THE ENFORCEMENT. The server refuses the combination
  // independently and in both directions (see `CreateAppointmentDto.Recurrence`)
  // because a series would otherwise send one real email invitation per
  // occurrence. What this buys is that the refusal is explained where the
  // choice is made, instead of arriving as a failed save.
  //
  // Each control disables the other, EXCEPT when both are already set — which
  // only an AI-parsed draft can produce, and where disabling both would trap
  // the user in a state the server will reject. There, both stay live and
  // submit refuses with the reason.
  const hasExternalAttendees = externalAttendees.length > 0;
  const repeats = repeatRule !== null;
  const repeatAndExternalsBothSet = repeats && hasExternalAttendees;
  const repeatDisabled = hasExternalAttendees && !repeatAndExternalsBothSet;
  const externalsDisabled = repeats && !repeatAndExternalsBothSet;

  // A weekly rule with nothing ticked cannot be expanded; the builder says so
  // in place, and submit refuses rather than sending a rule the server rejects.
  const repeatRuleIncomplete =
    repeatRule?.frequency === "Weekly" &&
    (repeatRule.byWeekday ?? "").split(",").filter(Boolean).length === 0;

  // --- Attendee conflict warning + availability strip -----------------------
  // Both informational; neither ever blocks save.
  //
  // The (undebounced) payloads for the current draft. Null disables the
  // corresponding request. Both share the trigger condition — the modal is
  // open, a Start date is set, and at least one Team Member is selected — so a
  // solo appointment triggers neither. The organizer rides the userIds set the
  // same way an invited member does.
  //
  // They differ in window: the conflict check asks about the proposed meeting
  // (and so also needs valid, non-degenerate times), while the strip asks about
  // the whole organizer-local calendar day containing the Start date. That's
  // why they're two requests against two endpoints — a day-wide conflict check
  // would flag every same-day appointment.
  const liveLookups = useMemo<DraftLookups>(() => {
    if (!open) return NO_LOOKUPS;
    if (attendeeIds.length === 0) return NO_LOOKUPS;
    if (!startDate) return NO_LOOKUPS;

    // Dedup + sort so the query keys are stable under attendee reordering.
    const ids = [organizerId, appointment?.ownerUserId, ...attendeeIds].filter(
      (x): x is string => Boolean(x),
    );
    const userIds = Array.from(new Set(ids)).sort();
    if (userIds.length === 0) return NO_LOOKUPS;

    const effEndDate = endDate || startDate;
    // The window this check asks about must be the window that will be SAVED,
    // so it composes in the same zone the submit handler does. The availability
    // strip below deliberately stays on `tz`: that one asks about the whole
    // ORGANIZER-local day, which is a real instant window, not this
    // appointment's wall-calendar day.
    const startIso = wallClockToUtcIso(
      startDate,
      isAllDay ? "00:00" : startTime,
      dayZone,
    );
    const endIso = wallClockToUtcIso(
      effEndDate,
      isAllDay ? "23:59" : endTime,
      dayZone,
    );

    const conflict: AppointmentConflictCheckRequest | null =
      startIso &&
      endIso &&
      new Date(endIso).getTime() > new Date(startIso).getTime()
        ? {
            userIds,
            startUtc: startIso,
            endUtc: endIso,
            excludeAppointmentId: appointment?.id,
          }
        : null;

    // The full local day, midnight to midnight, computed with the same
    // wall-clock⇄UTC helper the form uses — so an organizer outside UTC gets
    // their own day, not a UTC-midnight window that would clip it.
    const dayStartIso = wallClockToUtcIso(startDate, "00:00", tz);
    const dayEndIso = wallClockToUtcIso(nextDayIso(startDate), "00:00", tz);

    const availability: AppointmentAvailabilityRequest | null =
      dayStartIso && dayEndIso
        ? {
            userIds,
            startUtc: dayStartIso,
            endUtc: dayEndIso,
            excludeAppointmentId: appointment?.id,
          }
        : null;

    // D7 — the bounded busy summary across the rule's next few occurrences.
    // 🔴 Gated on a repeat rule EXISTING, so a one-off appointment issues the
    // same two requests it always has; an unconditional third would break the
    // request-count invariant this form is pinned on.
    //
    // The rule is handed over as the form's own typed wall clock plus the
    // viewer's zone — deliberately NOT converted with `wallClockToUtcIso`, the
    // way the two windows above are. The server derives every occurrence.
    const durationMins = wallClockDeltaMinutes(startDate, startTime, effEndDate, endTime);
    const recurrence: RecurrenceConflictRequest | null =
      repeatRule && !isAllDay && durationMins !== null && durationMins > 0
        ? {
            userIds,
            // 🔴 `dayZone` IS `tz` HERE, ALWAYS — the guard above is
            // `!isAllDay`, so this branch cannot run for an all-day series and
            // the helper returns the viewer's zone for everything else. It is
            // written this way so the file has ONE zone expression across every
            // compose site rather than a `tz` that a later reader has to prove
            // is deliberate. A `isAllDay ? "00:00" : startTime` ternary was
            // tried here to match the submit handler and removed again: it
            // would be dead on this branch, and dead code that looks like a
            // rule is how the next person concludes the rule is different here.
            recurrence: buildRecurrenceRequest(
              repeatRule,
              recurrenceEnd,
              startDate,
              startTime,
              dayZone,
            ),
            durationMinutes: durationMins,
            excludeAppointmentId: appointment?.id ?? null,
            excludeSeriesId: appointment?.seriesId ?? null,
          }
        : null;

    return { conflict, availability, recurrence };
  }, [
    open,
    attendeeIds,
    startDate,
    startTime,
    endDate,
    endTime,
    isAllDay,
    tz,
    dayZone,
    organizerId,
    appointment?.id,
    appointment?.ownerUserId,
    appointment?.seriesId,
    repeatRule,
    recurrenceEnd,
  ]);

  // Debounce so a burst of edits (typing a time, adding members) fires one
  // batched pair of requests, not one per keystroke/attendee. Both land in the
  // same state update, so the two queries enable on the same render and run
  // concurrently.
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedLookups(liveLookups),
      CONFLICT_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [liveLookups]);

  const conflictQuery = useConflictCheck(debouncedLookups.conflict);
  const availabilityQuery = useAvailability(debouncedLookups.availability);
  const recurrenceConflictQuery = useRecurrenceConflicts(debouncedLookups.recurrence);

  const conflictsByUser = useMemo(() => {
    const map = new Map<string, ConflictingAppointmentDto[]>();
    for (const u of conflictQuery.data ?? []) map.set(u.userId, u.conflicts);
    return map;
  }, [conflictQuery.data]);

  // Per-attendee tooltip labels for the picker chips (Team Members only; the
  // organizer isn't a chip and surfaces via the summary line instead).
  const conflictLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const id of attendeeIds) {
      const conflicts = conflictsByUser.get(id);
      if (conflicts && conflicts.length > 0) map.set(id, describeConflict(conflicts, tz));
    }
    return map;
  }, [attendeeIds, conflictsByUser, tz]);

  const flaggedMemberCount = attendeeIds.filter((id) =>
    conflictsByUser.has(id),
  ).length;
  const organizerFlagged = Boolean(organizerId && conflictsByUser.has(organizerId));
  const conflictSummary = buildConflictSummary(flaggedMemberCount, organizerFlagged);

  // --- Availability strip rows ---------------------------------------------
  const busyByUser = useMemo(() => {
    const map = new Map<string, BusyBlockDto[]>();
    for (const u of availabilityQuery.data ?? []) map.set(u.userId, u.busyBlocks);
    return map;
  }, [availabilityQuery.data]);

  // Names for the strip's row labels. External attendees are structurally
  // absent here — rows come from internal user ids, and an external attendee
  // has none.
  const attendeeLabels = useInternalUserLabels(attendeeIds);
  const availabilityRows = useMemo<AvailabilityRow[]>(() => {
    const rows: AvailabilityRow[] = [];
    const seen = new Set<string>();
    const push = (userId: string | null | undefined, label: string, isOrganizer: boolean) => {
      if (!userId || seen.has(userId)) return;
      seen.add(userId);
      rows.push({ userId, label, isOrganizer });
    };

    // Rows mirror the requested userIds exactly, so we never fetch someone's
    // availability without showing it: the current user, the appointment's
    // owner when editing someone else's meeting, then the invited members.
    if (organizerId) {
      push(organizerId, auth?.user?.displayName ?? fallbackUserLabel(organizerId), true);
    }
    if (appointment?.ownerUserId) {
      push(
        appointment.ownerUserId,
        appointment.ownerDisplayName ?? fallbackUserLabel(appointment.ownerUserId),
        false,
      );
    }
    for (const id of attendeeIds) {
      push(id, attendeeLabels.lookup(id)?.displayName ?? fallbackUserLabel(id), false);
    }
    return rows;
  }, [
    organizerId,
    auth?.user?.displayName,
    appointment?.ownerUserId,
    appointment?.ownerDisplayName,
    attendeeIds,
    attendeeLabels,
  ]);

  const showAvailability =
    Boolean(debouncedLookups.availability) && availabilityRows.length > 0;

  // --- D7: the bounded busy summary across a repeat's next occurrences -------
  // One row per busy attendee under an aggregate headline. Users with no busy
  // occurrence are OMITTED by the endpoint — presence IS the signal, matching
  // the conflict badge's own contract — so an empty response renders nothing
  // and never reads as "we checked and found trouble".
  //
  // Advisory, like everything else on this form: it never disables Save.
  const recurrenceBusyRows = useMemo(() => {
    const rows: { userId: string; label: string; dates: string }[] = [];
    for (const u of (recurrenceConflictQuery.data ?? []) as UserRecurrenceConflictDto[]) {
      if (u.busyStartUtc.length === 0) continue;
      const label =
        u.userId === organizerId
          ? (auth?.user?.displayName ?? fallbackUserLabel(u.userId))
          : (attendeeLabels.lookup(u.userId)?.displayName ?? fallbackUserLabel(u.userId));
      // Named dates rather than a bare count: "busy on 3" is a number to go and
      // look up, and the whole point of checking a series in advance is to see
      // WHICH weeks clash before the invitations go out.
      const shown = u.busyStartUtc.slice(0, 4).map((iso) => formatDateInTz(iso, tz));
      const extra = u.busyStartUtc.length - shown.length;
      rows.push({
        userId: u.userId,
        label,
        dates: extra > 0 ? `${shown.join(", ")} +${extra} more` : shown.join(", "),
      });
    }
    return rows;
  }, [recurrenceConflictQuery.data, organizerId, auth?.user?.displayName, attendeeLabels, tz]);

  /** How many occurrences the server actually looked at — the "of N" in the headline. */
  const occurrencesChecked =
    (recurrenceConflictQuery.data ?? [])[0]?.occurrencesChecked ?? 0;
  const worstBusyCount = recurrenceBusyRows.length
    ? Math.max(
        ...((recurrenceConflictQuery.data ?? []) as UserRecurrenceConflictDto[]).map(
          (u) => u.busyStartUtc.length,
        ),
      )
    : 0;

  const videoLinkTooLong = videoLink.trim().length > MAX_VIDEO_LINK_LENGTH;

  /**
   * Sends an already-built update. Split out of {@link submit} so the scope
   * dialog can run the SAME payload a second time with a scope attached,
   * rather than rebuilding it from state that may have re-rendered underneath.
   */
  const runUpdate = async (payload: UpdateAppointmentDto) => {
    try {
      await update.mutateAsync(payload);
      toast({ message: "Appointment updated.", tone: "success" });
      onClose();
    } catch (err) {
      toast({ message: extractErrorMessage(err), tone: "error" });
    }
  };

  const submit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Title is required.");
      return;
    }
    if (!startDate) {
      setError("Start date is required.");
      return;
    }
    if (videoLinkTooLong) {
      // The inline message under the field is the one that names the limit;
      // this only stops the save. The server refuses it too.
      setError(null);
      return;
    }
    // D16 — only reachable from an AI-parsed draft that carried both, where
    // neither control is disabled precisely so this is fixable. The server
    // refuses the combination independently.
    if (repeatAndExternalsBothSet) {
      setError(
        "A repeating appointment can't have external attendees — an invitation would go out once per occurrence. Remove the guests or turn the repeat off.",
      );
      return;
    }
    if (repeatRuleIncomplete) {
      setError("Pick at least one day for the repeat.");
      return;
    }
    // An all-day appointment spans whole calendar days; clamp its instants to
    // the day bounds so the wall-clock time fields don't apply.
    const effEndDate = endDate || startDate;
    // `dayZone`, not `tz` — the same value hydration used. For an all-day
    // appointment being EDITED this round-trips the stored instants exactly, so
    // a save that touched only the title writes the identical window back.
    const startIso = wallClockToUtcIso(
      startDate,
      isAllDay ? "00:00" : startTime,
      dayZone,
    );
    const endIso = wallClockToUtcIso(
      effEndDate,
      isAllDay ? "23:59" : endTime,
      dayZone,
    );
    if (!startIso || !endIso) {
      setError("Enter a valid date and time.");
      return;
    }
    if (new Date(endIso).getTime() < new Date(startIso).getTime()) {
      setError("End must be on or after the start.");
      return;
    }
    setError(null);

    /**
     * The repeat rule on the wire, or null.
     *
     * 🔴 The anchor is the form's OWN typed date and time plus the viewer's
     * zone — handed over as a wall clock and NOT converted with
     * `wallClockToUtcIso` like `startIso`/`endIso` above. The server derives
     * every occurrence with `TimeZoneInfo`; converting here would resolve a
     * spring-forward gap an hour early and be wrong on every occurrence rather
     * than once. See the note on `wallClockToUtcIso` in ../lib/tz.ts.
     *
     * An all-day appointment anchors at midnight, matching the instants above.
     */
    const recurrencePayload: RecurrenceRequestDto | null = repeatRule
      ? buildRecurrenceRequest(
          repeatRule,
          recurrenceEnd,
          startDate,
          isAllDay ? "00:00" : startTime,
          dayZone,
        )
      : null;

    const createPayload: CreateAppointmentDto = {
      title: trimmedTitle,
      description: description.trim() || undefined,
      location: location.trim() || undefined,
      startAt: startIso,
      endAt: endIso,
      isAllDay,
      reminderMinutesBefore: reminder === "" ? undefined : reminder,
      relatedEntityType: linked?.type,
      relatedEntityId: linked?.id,
      attendeeUserIds: attendeeIds.length ? attendeeIds : undefined,
      videoLink: videoLink.trim() || undefined,
      dialInInstructions: dialIn.trim() || undefined,
      externalAttendees: externalAttendees.length ? externalAttendees : undefined,
      // Absent for a one-off, so a non-repeating create sends exactly what it
      // always has.
      recurrence: recurrencePayload ?? undefined,
      emailInternalAttendees: emailInternal,
    };

    // AI Create+ — the panel orchestrates the (optional Lead + appointment)
    // two-call flow and owns its own toast/close.
    if (onSubmitOverride) {
      setOverrideSubmitting(true);
      try {
        await onSubmitOverride(createPayload);
      } finally {
        setOverrideSubmitting(false);
      }
      return;
    }

    if (appointment) {
      const payload: UpdateAppointmentDto = {
        title: trimmedTitle,
        description: description.trim() || null,
        clearDescription: description.trim() === "",
        location: location.trim() || null,
        clearLocation: location.trim() === "",
        videoLink: videoLink.trim() || null,
        clearVideoLink: videoLink.trim() === "",
        dialInInstructions: dialIn.trim() || null,
        clearDialInInstructions: dialIn.trim() === "",
        startAt: startIso,
        endAt: endIso,
        isAllDay,
        reminderMinutesBefore: reminder === "" ? null : reminder,
        clearReminder: reminder === "",
        relatedEntityType: linked?.type,
        relatedEntityId: linked?.id,
        clearRelatedEntity: !linked,
        attendeeUserIds: attendeeIds,
        // Always sent in edit mode: the server treats null as "leave
        // unchanged", so omitting it when the list is empty would make
        // removing the last external attendee silently do nothing.
        externalAttendees,
        // Same reasoning, different mechanism: the server reads null as "leave
        // unchanged", and this form always knows the answer because it hydrates
        // the checkbox from the appointment. Sending the value explicitly is how
        // unticking it actually switches the choice off.
        emailInternalAttendees: emailInternal,
      };

      // The repeat rule travels only when its SHAPE changed — so an ordinary
      // edit (a retitle, a moved hour) carries neither `recurrence` nor
      // `clearRecurrence` and the series is left exactly as it was. Turning a
      // repeat off on a series clears it; turning one on for a one-off converts
      // it, which is what `UpdateAppointmentDto.recurrence` documents.
      const nextRuleKey = ruleShapeKey(repeatRule, recurrenceEnd);
      if (nextRuleKey !== initialRuleKey) {
        if (recurrencePayload) payload.recurrence = recurrencePayload;
        else payload.clearRecurrence = true;
      }

      // A rule change sent alongside `scope: "ThisOccurrence"` expresses two
      // things at once (move this meeting, and rewrite the series), and this
      // form deliberately sends both rather than quietly dropping half of what
      // the user expressed. The server reconciles them, and the two are not in
      // conflict: the scope governs which occurrences an edit's CONTENT reaches,
      // while a rule is a property of the SERIES with nowhere else to live, so a
      // rule change is always series-wide. See the recurrence phase of
      // AppointmentService.UpdateAsync.
      //
      // This is why `ruleShapeKey` excludes the anchor — otherwise every Start
      // edit on an occurrence would also read as a wholesale rule replacement.
      //
      // 🔴 The scope question is asked ONLY for an occurrence of a series. A
      // one-off appointment has no `seriesId`, so it saves on this click with
      // no dialog and no extra field — exactly as it always has.
      if (appointment.seriesId) {
        setPendingUpdate(payload);
        return;
      }
      await runUpdate(payload);
      return;
    }

    try {
      await create.mutateAsync(createPayload);
      toast({ message: "Appointment created.", tone: "success" });
      onClose();
    } catch (err) {
      toast({ message: extractErrorMessage(err), tone: "error" });
    }
  };

  const pending = create.isPending || update.isPending || overrideSubmitting;
  const showDateWarning = Boolean(prefill && !prefill.startUtc && !warningDismissed);

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={isEdit ? "Edit appointment" : "New appointment"}
      description="Schedule a meeting and invite teammates."
      size="md"
      footer={
        <>
          {onBack && (
            <Button variant="tertiary" onClick={onBack} type="button">
              Back
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={submit} loading={pending} type="button">
            {isEdit ? "Save changes" : "Create appointment"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        {showDateWarning && (
          <div className="flex items-start justify-between gap-space-2 rounded-md border border-gold/40 bg-gold/10 px-space-3 py-space-2 text-caption text-onyx">
            <span>AI couldn't detect a date — please set the start time.</span>
            <button
              type="button"
              onClick={() => setWarningDismissed(true)}
              aria-label="Dismiss"
              className="shrink-0 text-gray-mid hover:text-onyx"
            >
              <X size={14} aria-hidden />
            </button>
          </div>
        )}

        <FormField
          label="Title"
          required
          htmlFor="appt-title"
          error={error && !title.trim() ? error : undefined}
        >
          <Input
            id="appt-title"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What's the meeting?"
            invalid={Boolean(error && !title.trim())}
          />
        </FormField>

        <FormField label="Description" htmlFor="appt-desc">
          <Textarea
            id="appt-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional agenda, links, or notes (internal — not sent to external guests)."
          />
        </FormField>

        <FormField label="Location" htmlFor="appt-location">
          <Input
            id="appt-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Room, call link, or address."
          />
        </FormField>

        <FormField
          label="Video link"
          htmlFor="appt-video"
          error={
            videoLinkTooLong
              ? `Video link must be ${MAX_VIDEO_LINK_LENGTH} characters or fewer.`
              : undefined
          }
        >
          <Input
            id="appt-video"
            value={videoLink}
            onChange={(e) => setVideoLink(e.target.value)}
            placeholder="Zoom, Teams, Meet, or Webex URL."
          />
        </FormField>

        <FormField label="Dial-in" htmlFor="appt-dialin">
          <Textarea
            id="appt-dialin"
            value={dialIn}
            onChange={(e) => setDialIn(e.target.value)}
            placeholder="Phone number, meeting ID, passcode."
          />
        </FormField>

        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            className="accent-gold"
            checked={isAllDay}
            onChange={(e) => setIsAllDay(e.target.checked)}
          />
          All day
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <FormField label="Starts" htmlFor="appt-start-date">
            <div className="flex gap-space-2">
              <Input
                id="appt-start-date"
                type="date"
                value={startDate}
                onChange={(e) => applyStartChange(e.target.value, startTime)}
              />
              {!isAllDay && (
                <Input
                  type="time"
                  aria-label="Start time"
                  value={startTime}
                  onChange={(e) => applyStartChange(startDate, e.target.value)}
                />
              )}
            </div>
          </FormField>
          <FormField label="Ends" htmlFor="appt-end-date">
            <div className="flex gap-space-2">
              <Input
                id="appt-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              {!isAllDay && (
                <Input
                  type="time"
                  aria-label="End time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              )}
            </div>
          </FormField>
        </div>

        {/* Full-width rather than inside the "Ends" field: duration is a
            property of the pair, and the half-width column wraps six chips onto
            two rows. Hidden for an all-day appointment, which has no duration to
            pick — matching the time inputs above, which are hidden too. */}
        {!isAllDay && (
          <FormField label="Duration">
            <div className="flex flex-wrap items-center gap-space-2">
              <FilterChips
                label="Duration"
                chips={[...DURATION_PRESETS]}
                activeId={activeDurationId}
                onChange={(id) => {
                  const preset = DURATION_PRESETS.find((p) => p.id === id);
                  if (preset) applyDuration(preset.minutes);
                }}
              />
              {durationReadout && (
                <span className="ml-auto text-caption text-gray-mid tabular-nums">
                  {durationReadout}
                </span>
              )}
            </div>
          </FormField>
        )}

        {/* Full-width and a sibling of the Starts/Ends grid, matching the
            Duration row above: a repeat is a property of the appointment, not
            of either date field, and the half-width column would wrap the end
            condition onto its own line. */}
        <FormField label="Repeats" htmlFor="appt-repeat">
          <RepeatControl
            preset={repeatPreset}
            onPresetChange={setRepeatPreset}
            startDate={startDate}
            customRule={customRule}
            onCustomRuleChange={setCustomRule}
            end={recurrenceEnd}
            onEndChange={setRecurrenceEnd}
            rule={repeatRule}
            disabled={repeatDisabled}
            disabledReason={
              repeatDisabled
                ? "Repeats are off while this meeting has external attendees — an invitation would be emailed once per occurrence."
                : undefined
            }
          />
        </FormField>

        {error && title.trim() && startDate && (
          <p className="text-label text-status-error m-0" role="alert">
            {error}
          </p>
        )}

        {/*
          The old helper text — "An in-app heads-up for the team; external
          attendees get an email" — stopped being true the moment internal
          attendees could be emailed too, and it was the one line on this form
          that told the user which audience got which channel.
        */}
        <FormField
          label="Reminder"
          htmlFor="appt-reminder"
          helperText="An in-app heads-up before the meeting. Who gets an email is set below."
        >
          <Select
            id="appt-reminder"
            value={reminder === "" ? "" : String(reminder)}
            onChange={(e) =>
              setReminder(e.target.value === "" ? "" : Number(e.target.value))
            }
          >
            {REMINDER_OPTIONS.map((o) => (
              <option key={o.label} value={o.value === "" ? "" : String(o.value)}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormField>

        {/*
          Sits between Reminder and Team members because it qualifies BOTH: it is
          a delivery choice, and the people it delivers to are the ones picked
          immediately below.

          Matches the All-day control's shape — a bare <label> wrapping a native
          checkbox, not a FormField — because the label IS the whole question and
          a FormField would add a second one above it.
        */}
        <label className="flex items-start gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            className="accent-gold mt-[3px]"
            checked={emailInternal}
            onChange={(e) => setEmailInternal(e.target.checked)}
          />
          <span>
            Email the team the meeting details
            <span className="block text-caption text-gray-mid">
              Sends teammates the same invitation external attendees get, with the
              join link and a calendar file — and tells them if it moves or is
              cancelled.
            </span>
          </span>
        </label>

        <FormField
          label="Team members"
          helperText="Invite internal teammates. They'll see it on their calendar."
        >
          <InternalUserPicker
            value={attendeeIds}
            onChange={setAttendeeIds}
            conflictLabels={conflictLabels}
          />
        </FormField>

        {showAvailability && (
          <FormField
            label="Availability"
            helperText="Busy time on this day, in your timezone. Advisory — it never blocks scheduling."
          >
            <AttendeeAvailabilityStrip
              dayIso={startDate}
              tz={tz}
              rows={availabilityRows}
              busyByUser={busyByUser}
              loading={availabilityQuery.isLoading}
            />
          </FormField>
        )}

        <FormField
          label="External attendees"
          helperText={
            externalsDisabled
              ? "Not available on a repeating appointment — an invitation would be emailed once per occurrence. Turn the repeat off to invite a guest."
              : isEdit
                ? "Non-staff guests. Anyone you add is emailed an invitation; anyone you remove is emailed a cancellation. Set someone's time zone only if you know it — their invitation will show them their own hour."
                : "Non-staff guests. They'll get an email with the meeting details on creation. Set someone's time zone only if you know it — their invitation will show them their own hour."
          }
        >
          {/* D16 — disabled from the OUTSIDE, via a native disabled fieldset,
              because the editor takes no `disabled` prop and lives in another
              lane's file. A fieldset disables every control it contains, which
              is exactly the whole editor.

              🔴 A COURTESY, NOT THE ENFORCEMENT: the server refuses a repeat
              alongside external attendees on its own, in both directions. This
              only moves the refusal to where the choice is made. */}
          <fieldset disabled={externalsDisabled} className="m-0 p-0 border-0">
            <legend className="sr-only">External attendees</legend>
            <ExternalAttendeeEditor
              value={externalAttendees}
              onChange={setExternalAttendees}
            />
          </fieldset>
        </FormField>

        <FormField
          label="Linked record"
          helperText={
            linkLocked
              ? "Linked to the record you're scheduling from."
              : "Optional. Link this appointment to any record in the system."
          }
        >
          {linked ? (
            <div className="flex items-center justify-between gap-space-3 rounded-md bg-white-warm border border-border-light px-space-3 py-space-2 text-small text-onyx">
              {/* Opens in a new tab, matching PlacementChip and the messaging
                  ResourceChip. Same-tab navigation would unmount this modal and
                  silently discard any unsaved edits — the shared Modal funnels
                  every close path into one `onClose` and tracks no dirty state,
                  so there is nothing that would catch it. */}
              {linked.detailUrl ? (
                <a
                  href={linked.detailUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`${linked.label} (opens in new tab)`}
                  className="truncate text-gold-shadow hover:underline underline-offset-4"
                >
                  {linked.label}
                </a>
              ) : (
                <span className="truncate">{linked.label}</span>
              )}
              {/* A pre-seeded link is the whole point of scheduling from an
                  entity page, so it has no remove affordance — the record can
                  still be changed by cancelling and using the calendar. */}
              {!linkLocked && (
                <button
                  type="button"
                  onClick={() => setLinked(null)}
                  aria-label="Remove linked record"
                  className="shrink-0 text-gray-mid hover:text-onyx"
                >
                  <X size={14} aria-hidden />
                </button>
              )}
            </div>
          ) : (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPickerOpen(true)}
            >
              + Link a record
            </Button>
          )}
        </FormField>

        {conflictSummary && (
          <div
            className="flex items-start gap-space-2 rounded-md border border-status-warning bg-status-warning/10 px-space-3 py-space-2 text-caption text-onyx"
            role="status"
          >
            <AlertTriangle
              size={14}
              className="mt-[1px] shrink-0 text-status-warning"
              aria-hidden
            />
            <span>{conflictSummary}</span>
          </div>
        )}

        {recurrenceBusyRows.length > 0 && (
          <div
            className="flex flex-col gap-space-1 rounded-md border border-status-warning bg-status-warning/10 px-space-3 py-space-2 text-caption text-onyx"
            role="status"
          >
            <div className="flex items-start gap-space-2">
              <AlertTriangle
                size={14}
                className="mt-[1px] shrink-0 text-status-warning"
                aria-hidden
              />
              <span>
                Busy on {worstBusyCount} of the next {occurrencesChecked}{" "}
                {occurrencesChecked === 1 ? "occurrence" : "occurrences"}.
              </span>
            </div>
            {recurrenceBusyRows.map((r) => (
              <span key={r.userId} className="pl-[22px] text-gray-mid">
                {r.label}: {r.dates}
              </span>
            ))}
          </div>
        )}

        {extraContent}
      </div>

      {/* 🔴 Rendered only for an occurrence of a series — `pendingUpdate` is
          never set for a one-off, so a one-off save never sees this and never
          gains a click. */}
      <RecurrenceScopeDialog
        open={pendingUpdate !== null}
        action="edit"
        /* The number the "All" option quietly would NOT reach. Read straight
           off the series summary — never counted client-side, which could only
           see the occurrences inside the loaded window. 0 for a one-off, where
           the dialog does not render at all. */
        detachedCount={appointment?.recurrence?.detachedCount ?? 0}
        pending={update.isPending}
        onCancel={() => setPendingUpdate(null)}
        onChoose={(scope: RecurrenceEditScope) => {
          const payload = pendingUpdate;
          setPendingUpdate(null);
          if (payload) void runUpdate({ ...payload, scope });
        }}
      />

      <AttachResourceModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(attachment) => {
          const type = taskTypeForMessageType(attachment.resourceType);
          if (!type) return;
          setLinked({
            type,
            id: attachment.resourceId,
            label: attachment.displayLabel,
            // Every registered picker resolves a detail route, so this is
            // always populated on a fresh pick.
            detailUrl: attachment.detailUrl,
          });
        }}
      />
    </Modal>
  );
}
